import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import {
  ColumnValuesPayload,
  ColumnValuesResponse,
  GetParams,
  GetResponse,
  Partner,
  PartnerType,
  PatchPartnerPayload,
  PostResponse,
  SetParentOperation,
  SetParentResult,
  User,
  UserCreate,
} from '@app/shared';
import { convertGetParamsToHttpParams } from '@app/core';

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  /** URLs principales relacionadas con Partners **/
  partnerUrl = environment.coreApiBaseUrl + '/api/partner';
  partnerTypeUrl = environment.coreApiBaseUrl + '/api/partner-type';
  userUrl = environment.identityApiBaseUrl + '/api/user';
  private companiesRequest$?: Observable<Partner[]>;

  constructor(private http: HttpClient) {}

  /** Metodos de obtener/crear/actualizar/eliminar Partners **/
  getPartner(
    getParams: GetParams,
    accessToken?: string
  ): Observable<GetResponse> {
    let headers = new HttpHeaders();

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.partnerUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers, // Pasamos los headers
    });
  }

  createPartner(newPartner: FormData): Observable<PostResponse> {
    const headers = new HttpHeaders({ enctype: 'multipart/form-data' });
    return this.http.post<PostResponse>(this.partnerUrl, newPartner, {
      headers,
    }).pipe(tap(() => this.clearCompaniesCache()));
  }

  updatePartner(partner: FormData): Observable<any> {
    const headers = new HttpHeaders({ enctype: 'multipart/form-data' });
    return this.http.put(this.partnerUrl, partner, { headers }).pipe(
      tap(() => this.clearCompaniesCache())
    );
  }

  /**
   * Actualizacion PARCIAL de un Partner. Cuerpo JSON: el middleware
   * PatchRequestPropertyExtractorMiddleware del core solo lee los nombres de
   * propiedad cuando el Content-Type es application/json, y solo entonces el
   * backend distingue "no enviado" de "enviado a null".
   *
   * NO usar updatePartner() (PUT multipart) para cambios parciales: ese endpoint
   * reemplaza el registro completo y vaciaria el resto de campos del contacto.
   */
  patchPartner(payload: PatchPartnerPayload): Observable<any> {
    return this.http
      .patch(this.partnerUrl, payload)
      .pipe(tap(() => this.clearCompaniesCache()));
  }

  deletePartner(partnersIds: number[]): Observable<any> {
    return this.http.delete(this.partnerUrl, { body: { ids: partnersIds } }).pipe(
      tap(() => this.clearCompaniesCache())
    );
  }

  /** Obtener solo empresas (isCompany = true). Cacheado para que Empresa Cliente se muestre al instante en el drawer. */
  getCompanies(): Observable<Partner[]> {
    if (this.companiesRequest$) {
      return this.companiesRequest$;
    }

    const params: GetParams = {
      startRow: 0,
      endRow: 99999999,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'isCompany',
      filterValue: 'true',
    } as any;
    this.companiesRequest$ = this.getPartner(params).pipe(
      map((resp: GetResponse) => resp?.data?.rows || []),
      shareReplay(1)
    );

    return this.companiesRequest$;
  }

  private clearCompaniesCache(): void {
    this.companiesRequest$ = undefined;
  }

  /** POST avanzado para filtrar partners (ej. por parentId) */
  postAdvanced(body: any): Observable<Partner[]> {
    const url = `${this.partnerUrl}/get/advanced`;
    return this.http
      .post<GetResponse>(url, body)
      .pipe(map((r: GetResponse) => r?.data?.rows || []));
  }

  /** Empleados asociados a una empresa (relacion inversa de parentId). */
  getEmployeesByCompany(companyId: number): Observable<Partner[]> {
    return this.postAdvanced({
      filterGroups: [
        [
          { property: 'isCompany', value: 'false', operator: 'Equals' },
          { property: 'parentId', value: String(companyId), operator: 'Equals' },
        ],
      ],
      startRow: 0,
      endRow: 99999999,
      sortDirection: 'asc',
      sortProperty: 'name',
    });
  }

  /**
   * Empleados disponibles para asociar: los de tipo empleado que no pertenecen
   * todavia a ninguna empresa. El filtro parentId = null lo resuelve el backend
   * como "ParentId IS NULL" (AdvancedFilterUtility.CreateNullableFilterExpression),
   * asi que las reglas "solo empleados", "no reasociar" y "sin duplicados" se
   * cumplen en origen y el front no las reimplementa.
   */
  getAvailableEmployees(): Observable<Partner[]> {
    return this.postAdvanced({
      filterGroups: [
        [
          { property: 'employee', value: 'true', operator: 'Equals' },
          { property: 'parentId', value: null, operator: 'Equals' },
        ],
      ],
      startRow: 0,
      endRow: 99999999,
      sortDirection: 'asc',
      sortProperty: 'name',
    });
  }

  /**
   * Unico punto de asociacion/desasociacion de empleados a una empresa.
   * parentId con valor asocia; parentId null desasocia (no borra el contacto).
   *
   * Hoy se resuelve con N llamadas PATCH. NO se usa forkJoin "a pelo" a proposito:
   * forkJoin aborta las peticiones hermanas en cuanto una falla, lo que dejaria
   * el lote a medias sin saber que se aplico. Cada operacion captura su propio
   * error, se esperan TODAS y se devuelve el detalle por operacion, para que la
   * UI pueda decir exactamente que paso y por que.
   *
   * Cuando exista el endpoint en lote POST /api/partner/set-parent (CEC-598) se
   * sustituye SOLO el cuerpo de este metodo mapeando su respuesta a SetParentResult:
   * al ser atomico, o todo cae en succeeded o todo cae en failed.
   */
  setEmployeesParent(
    operations: SetParentOperation[]
  ): Observable<SetParentResult> {
    if (!operations.length) {
      return of({ succeeded: [], failed: [] });
    }

    const runOperation = (
      operation: SetParentOperation
    ): Observable<{ partnerId: number; reason: string | null }> =>
      this.patchPartner({
        id: operation.partnerId,
        parentId: operation.parentId,
      }).pipe(
        map(() => ({ partnerId: operation.partnerId, reason: null })),
        catchError((e: any) =>
          of({
            partnerId: operation.partnerId,
            reason: e?.error?.message || 'Error desconocido',
          })
        )
      );

    return forkJoin(operations.map(runOperation)).pipe(
      map((results) => ({
        succeeded: results
          .filter((r) => r.reason === null)
          .map((r) => r.partnerId),
        failed: results
          .filter(
            (r): r is { partnerId: number; reason: string } => r.reason !== null
          )
          .map((r) => ({ partnerId: r.partnerId, reason: r.reason })),
      }))
    );
  }

  getPartnerColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.partnerUrl}/column-values`,
      payload
    );
  }

  /** Metodos de obtener/crear/actualizar/eliminar PartnerTypes **/
  getPartnerType(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.partnerTypeUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createPartnerType(newPartnerType: {
    name: string;
    isActive: boolean;
  }): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.partnerTypeUrl, newPartnerType);
  }

  updatePartnerType(partnerType: PartnerType): Observable<any> {
    return this.http.put(this.partnerTypeUrl, partnerType);
  }

  deletePartnerType(partnerTypesIds: number[]): Observable<any> {
    return this.http.delete(this.partnerTypeUrl, {
      body: { ids: partnerTypesIds },
    });
  }

  /** Metodos de obtener/crear/actualizar/eliminar User **/
  getUser(getParams: GetParams, accessToken?: string): Observable<GetResponse> {
    let headers = new HttpHeaders();

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.get<GetResponse>(this.userUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers,
    });
  }

  createUser(newUser: UserCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.userUrl, newUser);
  }

  updateUser(User: User): Observable<any> {
    return this.http.put(this.userUrl, User);
  }

  deleteUser(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.userUrl, {
      body: { ids: desconsolidationsTypeIds },
    });
  }
}
